import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {createPointMask} from '@Libraries/point-mask';
import MaskingImageContainer from '@Panels/masking-panel/components/masking-image-container';
import {EditAction, ZoomType} from '@Panels/masking-panel/components/masking-image-container/masking-image-container';
import {setRedo, setUndo, setZoomIn, setZoomOut} from '@Panels/masking-panel/components/freehand-masking-panel/freehand-masking-panel-reducer';
import type {CropData} from '@PosterWhiteboard/classes/item-crop.class';
import type {MaskingItemObject} from '@PosterWhiteboard/classes/item-masking.class';
import styles from './freehand-masking-canvas.module.scss';

export interface FreehandMaskingCanvasProps {
  id: string;
  url: string;
  initialMaskingData?: MaskingItemObject;
  cropData?: CropData;
  isSmallScreen: boolean;
}

const parentSelector = 'freehand-masking-container';

function FreehandMaskingCanvas({...props}: FreehandMaskingCanvasProps): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const initCanvas = (): void => {
    createPointMask(props.id, props.url, `.${parentSelector}`, {cropData: props.cropData, initialMaskingData: props.initialMaskingData})
      .then((pointMaskCanvas) => {
        window.pointMask = pointMaskCanvas;
        window.PMW.redux.store.dispatch(setUndo(!window.pointMask.isUndoAvailable()));
        window.PMW.redux.store.dispatch(setRedo(!window.pointMask.isRedoAvailable()));
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const updateZoomBtnState = (): void => {
    if (window.pointMask) {
      window.PMW.redux.store.dispatch(setZoomIn(window.pointMask.isMaxZoomed()));
      window.PMW.redux.store.dispatch(setZoomOut(window.pointMask.isMinZoomed()));
    }
  };

  const onZoom = (zoom: ZoomType): void => {
    if (zoom === ZoomType.ZOOM_OUT && window.pointMask) {
      window.pointMask.decreaseZoom();
    } else if (zoom === ZoomType.ZOOM_IN && window.pointMask) {
      window.pointMask.increaseZoom();
    } else {
      throw new Error('Unexpected Zoom type');
    }
    updateZoomBtnState();
  };

  const onUndoRedo = (actionType: EditAction): void => {
    if (actionType === EditAction.UNDO && window.pointMask) {
      window.pointMask.onUndo();
    } else if (actionType === EditAction.REDO && window.pointMask) {
      window.pointMask.onRedo();
    } else {
      throw new Error('Unexpected edit action');
    }
  };

  useEffect(initCanvas, []);

  return (
    <MaskingImageContainer
      zoomCallback={onZoom}
      undoRedoCallback={onUndoRedo}
      isSmall={props.isSmallScreen}
      additionalClasses={`${props.isSmallScreen ? '' : styles.maskingImageContainer} ${parentSelector}`}
    >
      <canvas id={props.id} ref={canvasRef} />
    </MaskingImageContainer>
  );
}

export default FreehandMaskingCanvas;
